import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import {
  NotificationActionType,
  useAcknowledgeNotification,
  useArchiveNotification,
  UserNotificationWithDetails,
  useUserNotifications,
} from 'hooks/useUserNotifications';
import {
  NotificationWithMetadataDto,
  useArchiveNotificationForAll,
  useNotificationsByGroup,
  NotificationPriority,
} from 'hooks/useNotification';
import { Card } from 'react-bootstrap';
import { selectUserGuid, useAppSelector } from 'slices/commonSelectors';
import NotificationsFilterBar from './NotificationsFilterBar';
import { useMemo, useState } from 'react';
import ReviewRequestedLink from './ReviewRequestedNavlink';
import { useParams } from 'react-router';
import { useActivationActions } from 'hooks/incident/useActivationActions';

type NotificationsFilter = 'active' | 'historical' | 'sent';

type NotificationsListProps = {
  filter: NotificationsFilter;
};

const getSeverityBadgeClass = (priority: string | undefined): string => {
  if (!priority) return 'bg-secondary';

  switch (priority.toLowerCase()) {
    case NotificationPriority.CRITICAL:
      return 'bg-danger';
    case NotificationPriority.WARNING:
      return 'bg-warning text-dark';
    case NotificationPriority.WATCH:
      return 'bg-info';
    case NotificationPriority.ADVISORY:
      return 'bg-primary';
    case NotificationPriority.INFO:
      return 'bg-secondary';
    default:
      return 'bg-secondary';
  }
};

const NotificationsList: React.FC<NotificationsListProps> = ({ filter }) => {
  const userGuid = useAppSelector(selectUserGuid);
  const { incidentId } = useParams<{ incidentId: string }>();
  const {
    data: userNotifications = [],
    isLoading: isUserNotificationsLoading,
  } = useUserNotifications();
  const { data: notifications = [], isLoading: isNotificationsLoading } =
    useNotificationsByGroup();
  const acknowledgeNotificationMutation = useAcknowledgeNotification();
  const isLoading = isNotificationsLoading || isUserNotificationsLoading;
  const archiveNotificationForAllMutation = useArchiveNotificationForAll();
  const archiveUserNotification = useArchiveNotification();
  const { mutateAsync: handleActivationUpdate } = useActivationActions();

  // Filtering
  const [searchText, setSearchText] = useState('');
  const [selectedAorId, setSelectedAorId] = useState<UUID | null>(null);
  const [selectedIncidentId, setSelectedIncidentId] = useState<UUID | null>(
    incidentId ?? null
  );
  const filteredUserNotifications = useMemo(() => {
    let result = userNotifications.filter((un) =>
      filter === 'historical' ? un.archived : !un.archived
    );
    const query = searchText.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (item) =>
          (item.appMessage || '').toLowerCase().includes(query) ||
          (item.createdByName || '').toLowerCase().includes(query)
      );
    }

    if (selectedAorId) {
      result = result.filter((n) => n.aors?.includes(selectedAorId));
    }

    if (selectedIncidentId) {
      result = result.filter((n) => n.incidentId === selectedIncidentId);
    }
    return result;
  }, [
    userNotifications,
    filter,
    searchText,
    selectedAorId,
    selectedIncidentId,
  ]);

  const filteredSentNotifications = useMemo(() => {
    let result = notifications.filter((n) => n.createdBy === userGuid);
    const query = searchText.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (item) =>
          (item.metadata?.emailSubject || '').toLowerCase().includes(query) ||
          (item.metadata?.emailMessage || '').toLowerCase().includes(query) ||
          (item.metadata?.appMessage || '').toLowerCase().includes(query) ||
          (item.metadata?.teamsMessage || '').toLowerCase().includes(query) ||
          (item.metadata?.smsMessage || '').toLowerCase().includes(query)
      );
    }

    if (selectedAorId) {
      result = result.filter((n) => n.aors?.includes(selectedAorId));
    }

    if (selectedIncidentId) {
      result = result.filter((n) => n.incidentId === selectedIncidentId);
    }

    result.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });

    return result;
  }, [notifications, searchText, userGuid, selectedAorId, selectedIncidentId]);

  const handleAcknowledgement = async (notificationId: string) => {
    await acknowledgeNotificationMutation.mutateAsync({ notificationId });
  };

  const handleArchiveForAll = async (notificationId: string) => {
    await archiveNotificationForAllMutation.mutateAsync({ notificationId });
  };

  const handleActivation = async (
    notification: UserNotificationWithDetails
  ) => {
    if (!notification.incidentId) return;

    await handleActivationUpdate({
      incidentId: notification.incidentId,
      memberUserGuid: userGuid,
      isActivated: true,
    });

    await archiveUserNotification.mutateAsync({
      notificationId: notification.notificationId,
    });
  };

  const formatTimestamp = (
    value: Date | string | number | null | undefined
  ) => {
    if (!value) return 'Unknown time';
    const formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
    return formatter.format(new Date(value)).replace(',', '');
  };

  const renderUserNotification = (item: UserNotificationWithDetails) => {
    return (
      <Card key={item.notificationId} className="card-outlined mb-2">
        <Card.Body>
          <div className="mb-1 d-flex align-items-center gap-2">
            {item.priority && (
              <span
                className={`badge ${getSeverityBadgeClass(item.priority)} text-uppercase`}
                style={{ fontSize: '0.65rem' }}
              >
                {item.priority === NotificationPriority.CRITICAL
                  ? 'Stand Down Safety'
                  : item.priority}
              </span>
            )}
            <span>{item.appMessage || 'Notification'}</span>
          </div>
          <div className="text-muted small">
            <span>{item.createdByName || 'System'}</span>
            <span className="mx-2">•</span>
            <span>{formatTimestamp(item.sentAt)}</span>
          </div>
          <div className="d-flex gap-2 mt-2">
            {item.acknowledgementRequired && !item.acknowledged && (
              <StylishNewButton
                className="button--primary button--sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAcknowledgement(item.notificationId);
                }}
              >
                Acknowledge
              </StylishNewButton>
            )}
          </div>

          <div
            className="notification-content mt-2"
            style={{ marginBottom: '-0.5rem' }}
          >
            {item.sourceItemId && <ReviewRequestedLink {...item} />}

            {item.actionType === NotificationActionType.ACTIVATION_REQUEST &&
              !item.archivedAt && (
                <div className="d-flex gap-2 mt-2">
                  <StylishNewButton
                    className="button--primary button--sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleActivation(item);
                    }}
                  >
                    Activate
                  </StylishNewButton>
                </div>
              )}

            <div className="mt-2 small text-muted">
              {item.acknowledged && (
                <span className="me-3">
                  Acknowledged {formatTimestamp(item.acknowledgedAt)}
                </span>
              )}
              {item.archived && <span>Archived</span>}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderSentNotification = (item: NotificationWithMetadataDto) => {
    const notificationId = item.id;
    const createdAt = item.createdAt;
    const title = `Notification sent ${formatTimestamp(createdAt)}`;

    return (
      <Card key={notificationId} className="card-outlined mb-2">
        <Card.Body>
          <div className="mb-1 d-flex align-items-center gap-2">
            {item.priority && (
              <span
                className={`badge ${getSeverityBadgeClass(item.priority)} text-uppercase`}
                style={{ fontSize: '0.65rem' }}
              >
                {item.priority.toLowerCase() === 'critical'
                  ? 'Stand Down Safety'
                  : item.priority}
              </span>
            )}
            <span>{title}</span>
          </div>

          <div
            className="notification-content mt-3"
            style={{ marginBottom: '-0.75rem' }}
          >
            {item.metadata?.appMessage && (
              <div className="mb-3">
                <div className="fw-semibold text-muted small mb-1">
                  <i
                    className="material-symbols-outlined me-1"
                    style={{ fontSize: '1rem', verticalAlign: 'middle' }}
                  >
                    notifications
                  </i>
                  In-App Message
                </div>
                <p className="mb-0">{item.metadata.appMessage}</p>
              </div>
            )}

            {item.metadata?.emailMessage && (
              <div className="mb-3">
                <div className="fw-semibold text-muted small mb-1">
                  <i
                    className="material-symbols-outlined me-1"
                    style={{ fontSize: '1rem', verticalAlign: 'middle' }}
                  >
                    email
                  </i>
                  Email Message
                </div>
                {item.metadata.emailSubject && (
                  <div className="fw-semibold mb-1">
                    {item.metadata.emailSubject}
                  </div>
                )}
                <p className="mb-0">{item.metadata.emailMessage}</p>
              </div>
            )}

            {item.metadata?.smsMessage && (
              <div className="mb-3">
                <div className="fw-semibold text-muted small mb-1">
                  <i
                    className="material-symbols-outlined me-1"
                    style={{ fontSize: '1rem', verticalAlign: 'middle' }}
                  >
                    sms
                  </i>
                  SMS Message
                </div>
                <p className="mb-0">{item.metadata.smsMessage}</p>
              </div>
            )}

            {item.metadata?.teamsMessage && (
              <div className="mb-3">
                <div className="fw-semibold text-muted small mb-1">
                  <i
                    className="material-symbols-outlined me-1"
                    style={{ fontSize: '1rem', verticalAlign: 'middle' }}
                  >
                    groups
                  </i>
                  Teams Message
                </div>
                <p className="mb-0">{item.metadata.teamsMessage}</p>
              </div>
            )}

            {!item.metadata?.appMessage &&
              !item.metadata?.emailMessage &&
              !item.metadata?.smsMessage &&
              !item.metadata?.teamsMessage && (
                <p className="text-muted">No message content available</p>
              )}
          </div>

          {item.hasUnarchivedRecipients && (
            <StylishNewButton
              className="button--primary button--sm mt-2"
              onClick={(e) => {
                e.stopPropagation();
                handleArchiveForAll(item.id);
              }}
            >
              Archive for All Recipients
            </StylishNewButton>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <div>
      <NotificationsFilterBar
        searchText={searchText}
        onSearchChange={setSearchText}
        selectedAorId={selectedAorId}
        onAorChange={setSelectedAorId}
        selectedIncidentId={selectedIncidentId}
        onIncidentChange={setSelectedIncidentId}
      />

      <div className="notifications-list">
        {isLoading ? (
          <div>Loading...</div>
        ) : filter === 'sent' ? (
          filteredSentNotifications.length ? (
            filteredSentNotifications.map(renderSentNotification)
          ) : (
            <div>No notifications</div>
          )
        ) : filteredUserNotifications.length ? (
          filteredUserNotifications.map(renderUserNotification)
        ) : (
          <div>No notifications</div>
        )}
      </div>
    </div>
  );
};

export default NotificationsList;
